<template>
    <transition name="christmas-greeting">
        <section v-if="christmasSeason && !docked" class="christmas-greeting" aria-label="Mensagem de festas">
            <span ref="star" class="christmas-greeting-icon" aria-hidden="true">
                <svg viewBox="-12 -12 24 24">
                    <path :d="starPath"/>
                </svg>
            </span>

            <p class="christmas-greeting-text">
                <strong><span class="christmas-greeting-gold">{{ greeting }}</span>{{ firstName ? `, ${firstName}` : '' }}!</strong>
                <span class="christmas-greeting-message">{{ message }}</span>
                <span class="christmas-greeting-sign">Com carinho, equipe R2 Soft.</span>
            </p>

            <!-- Enfeites pendurados (tampa dourada, faixa e reflexo): só decoração -->
            <div class="christmas-greeting-ornaments" aria-hidden="true">
                <svg v-for="ornament in ornaments" :key="ornament.id" class="christmas-greeting-ornament"
                     :style="{left: ornament.left, '--delay': ornament.delay}"
                     :viewBox="`-14 ${-ornament.length - 18} 28 ${ornament.length + 32}`"
                     width="18" :height="(ornament.length + 32) * 18 / 28">
                    <defs>
                        <radialGradient :id="`greeting-bauble-${ornament.id}`" cx="0.32" cy="0.3" r="0.85">
                            <stop offset="0" :stop-color="ornament.light"/>
                            <stop offset="0.55" :stop-color="ornament.color"/>
                            <stop offset="1" :stop-color="ornament.deep"/>
                        </radialGradient>
                        <linearGradient :id="`greeting-gold-${ornament.id}`" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0" stop-color="#f8e7b0"/>
                            <stop offset="0.5" stop-color="#d4a64a"/>
                            <stop offset="1" stop-color="#a87a2a"/>
                        </linearGradient>
                    </defs>
                    <line x1="0" :y1="-ornament.length - 18" x2="0" :y2="ornament.star ? -11 : -16"
                          stroke="rgba(243, 214, 138, 0.45)" stroke-width="1"/>
                    <path v-if="ornament.star" :d="starPath" :fill="`url(#greeting-gold-${ornament.id})`"
                          class="christmas-greeting-ornament-star"/>
                    <template v-else>
                        <rect x="-3.5" y="-16.5" width="7" height="5" rx="1.2" :fill="`url(#greeting-gold-${ornament.id})`"/>
                        <circle cx="0" cy="0" r="12" :fill="`url(#greeting-bauble-${ornament.id})`"/>
                        <path d="M-11.6 2.5 C-6 5.5 6 5.5 11.6 2.5" fill="none" stroke="#f3d68a" stroke-width="1" opacity="0.75"/>
                        <ellipse cx="-4.2" cy="-5" rx="3.6" ry="2.2" fill="#ffffff" opacity="0.55" transform="rotate(-35 -4.2 -5)"/>
                    </template>
                </svg>
            </div>

            <button type="button" class="christmas-greeting-close" title="Guardar no cabeçalho"
                    aria-label="Guardar a mensagem de festas no cabeçalho" @click="dock">
                <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
            </button>
        </section>
    </transition>
</template>

<script>
// Cartão fino de festas no topo da home. Ao fechar, a estrela voa até o selo do cabeçalho; clicar no selo
// traz o cartão de volta. Se a mensagem do dia mudar, o cartão volta sozinho (ver christmasGreetingDocked).
import {mapMutations, mapState} from 'vuex';
import {STAR_PATH, prefersReducedMotion, saveChristmasGreetingDocked, seasonGreeting, seasonMessage} from '@/Helpers/christmas.js';

export default {
    name: 'ChristmasGreeting',
    data() {
        return {
            starPath: STAR_PATH,
            greeting: seasonGreeting(),
            message: seasonMessage(),
            firstName: (localStorage.getItem('user_name') || '').trim().split(/\s+/)[0] || '',
            // Mesma paleta dos enfeites do r2-oauth: dourado, esmeralda e bordô
            ornaments: [
                {id: 1, left: '0px', length: 6, delay: '0s', light: '#f8e7b0', color: '#d4a64a', deep: '#8a6420'},
                {id: 2, left: '22px', length: 16, delay: '-1.4s', star: true},
                {id: 3, left: '44px', length: 0, delay: '-0.7s', light: '#e48a92', color: '#9b1c2c', deep: '#4f0b15'},
            ],
        };
    },
    computed: {
        ...mapState({
            christmasSeason: 'christmasSeason',
            docked: 'christmasGreetingDocked',
        }),
    },
    methods: {
        ...mapMutations(['setChristmasGreetingDocked']),
        dock() {
            const from = this.$refs.star?.getBoundingClientRect();
            saveChristmasGreetingDocked(true);
            this.setChristmasGreetingDocked(true);
            this.$nextTick(() => this.flyToHeader(from));
        },
        // Uma estrela dourada sai do cartão e pousa no selo do cabeçalho
        flyToHeader(from) {
            const seal = document.querySelector('.christmas-seal');
            if (!from || !seal || prefersReducedMotion() || !seal.animate) return;
            const to = seal.querySelector('svg').getBoundingClientRect();
            const star = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            star.setAttribute('viewBox', '-12 -12 24 24');
            star.setAttribute('aria-hidden', 'true');
            star.classList.add('christmas-flying-star');
            star.innerHTML = `<path d="${STAR_PATH}"/>`;
            const size = 20;
            Object.assign(star.style, {
                left: `${from.left + from.width / 2 - size / 2}px`,
                top: `${from.top + from.height / 2 - size / 2}px`,
                width: `${size}px`,
                height: `${size}px`,
            });
            document.body.appendChild(star);
            const dx = to.left + to.width / 2 - (from.left + from.width / 2);
            const dy = to.top + to.height / 2 - (from.top + from.height / 2);
            const scale = to.width / size;
            star.animate([
                {transform: 'translate(0, 0) scale(1) rotate(0deg)', opacity: 1},
                {transform: `translate(${dx * 0.55}px, ${dy * 0.35 - 40}px) scale(1.4) rotate(160deg)`, opacity: 1, offset: 0.5},
                {transform: `translate(${dx}px, ${dy}px) scale(${scale}) rotate(360deg)`, opacity: 0.9},
            ], {duration: 850, easing: 'cubic-bezier(0.45, 0, 0.25, 1)'});
            // Rastro de faíscas douradas pelo caminho
            const trail = setInterval(() => {
                const box = star.getBoundingClientRect();
                const spark = document.createElement('span');
                spark.className = 'christmas-flying-spark';
                spark.style.left = `${box.left + box.width / 2 + (Math.random() - 0.5) * 8}px`;
                spark.style.top = `${box.top + box.height / 2 + (Math.random() - 0.5) * 8}px`;
                document.body.appendChild(spark);
                spark.addEventListener('animationend', () => spark.remove());
            }, 45);
            star.getAnimations()[0].onfinish = () => {
                clearInterval(trail);
                star.remove();
                seal.animate([
                    {transform: 'scale(1)'}, {transform: 'scale(1.12)'}, {transform: 'scale(1)'},
                ], {duration: 420, easing: 'ease-out'});
            };
        },
    },
};
</script>

<style scoped>
/* Mesmo acabamento do selo do cabeçalho: borda em degradê dourado, disco com aro dourado e um reflexo de luz */
.christmas-greeting {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-height: 54px;
    margin-bottom: 0.85rem;
    padding: 0.55rem 7.25rem 0.55rem 0.6rem;
    border: 1px solid transparent;
    border-radius: 16px;
    color: #ffffff;
    background:
        radial-gradient(circle at 86% 0%, rgba(226, 184, 92, 0.26), transparent 40%) padding-box,
        radial-gradient(circle at 0% 100%, rgba(45, 212, 191, 0.16), transparent 40%) padding-box,
        linear-gradient(135deg, #0f3f3a 0%, #115e55 55%, #0b3a33 100%) padding-box,
        linear-gradient(135deg, #f3d68a 0%, rgba(200, 151, 60, 0.55) 35%, rgba(246, 227, 168, 0.9) 62%, rgba(184, 134, 47, 0.5) 100%) border-box;
    box-shadow: 0 2px 4px rgba(4, 40, 35, 0.08), 0 12px 28px rgba(4, 40, 35, 0.18);
}

.christmas-greeting::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(105deg, transparent 35%, rgba(255, 244, 207, 0.16) 48%, transparent 61%);
    transform: translateX(-110%);
    animation: christmas-greeting-sweep 9s ease-in-out 1.5s infinite;
    pointer-events: none;
}

@keyframes christmas-greeting-sweep {
    0% { transform: translateX(-110%); }
    16%, 100% { transform: translateX(110%); }
}

.christmas-greeting-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #1d7a6c 0%, #0d4a42 60%, #082c27 100%);
    box-shadow: inset 0 0 0 1px rgba(243, 214, 138, 0.55), 0 0 0 4px rgba(243, 214, 138, 0.08), 0 2px 6px rgba(2, 6, 23, 0.3);
}

.christmas-greeting-icon svg {
    width: 1rem;
    height: 1rem;
    fill: #f3d68a;
    animation: christmas-greeting-twinkle 3.2s ease-in-out infinite alternate;
}

@keyframes christmas-greeting-twinkle {
    from { filter: drop-shadow(0 0 2px rgba(243, 214, 138, 0.4)); }
    to { filter: drop-shadow(0 0 8px rgba(243, 214, 138, 1)); }
}

.christmas-greeting-text {
    min-width: 0;
    margin: 0;
    overflow: hidden;
    color: rgba(255, 255, 255, 0.82);
    font-size: 0.86rem;
    line-height: 1.35;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.christmas-greeting-text strong {
    margin-right: 0.45rem;
    color: #ffffff;
    font-size: 0.95rem;
    font-weight: 800;
}

/* Saudação em dourado com brilho passando */
.christmas-greeting-gold {
    color: #f3d68a;
    background: linear-gradient(100deg, #e2b85c 0%, #e2b85c 38%, #fff4cf 50%, #e2b85c 62%, #e2b85c 100%);
    background-size: 260% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: christmas-greeting-shine 5.5s ease-in-out 1s infinite;
}

@keyframes christmas-greeting-shine {
    0% { background-position: 100% 0; }
    45%, 100% { background-position: 0 0; }
}

.christmas-greeting-sign {
    margin-left: 0.4rem;
    color: rgba(243, 214, 138, 0.8);
    font-style: italic;
}

.christmas-greeting-ornaments {
    position: absolute;
    top: 0;
    right: 3.4rem;
    width: 64px;
    height: 100%;
}

.christmas-greeting-ornament {
    position: absolute;
    top: 0;
    overflow: visible;
    transform-origin: top center;
    animation: christmas-greeting-sway 4.2s ease-in-out infinite alternate;
    animation-delay: var(--delay);
    filter: drop-shadow(0 4px 6px rgba(2, 6, 23, 0.3));
}

.christmas-greeting-ornament-star {
    filter: drop-shadow(0 0 3px rgba(243, 214, 138, 0.8));
}

@keyframes christmas-greeting-sway {
    from { transform: rotate(-6deg); }
    to { transform: rotate(6deg); }
}

.christmas-greeting-close {
    position: absolute;
    top: 50%;
    right: 0.7rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.9rem;
    height: 1.9rem;
    padding: 0;
    border: 1px solid rgba(243, 214, 138, 0.28);
    border-radius: 50%;
    color: rgba(255, 255, 255, 0.75);
    background: rgba(255, 255, 255, 0.06);
    transform: translateY(-50%);
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.christmas-greeting-close:hover,
.christmas-greeting-close:focus-visible {
    border-color: rgba(243, 214, 138, 0.75);
    color: #f3d68a;
    background: rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 0 3px rgba(243, 214, 138, 0.14);
    outline: none;
}

.christmas-greeting-enter-active,
.christmas-greeting-leave-active {
    transition: opacity 0.35s ease, transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), max-height 0.45s ease,
        margin 0.45s ease, padding 0.45s ease;
    max-height: 120px;
}

.christmas-greeting-enter-from,
.christmas-greeting-leave-to {
    max-height: 0;
    min-height: 0;
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
    opacity: 0;
    transform: translateY(-0.5rem) scale(0.98);
}

/* Telas menores: a mensagem quebra em até duas linhas e os enfeites saem */
@media (max-width: 991.98px) {
    .christmas-greeting {
        padding-right: 3rem;
    }

    .christmas-greeting-ornaments,
    .christmas-greeting-sign {
        display: none;
    }

    .christmas-greeting-text {
        white-space: normal;
    }

    .christmas-greeting-text strong {
        display: block;
    }
}

@media (prefers-reduced-motion: reduce) {
    .christmas-greeting::after,
    .christmas-greeting-icon svg,
    .christmas-greeting-gold,
    .christmas-greeting-ornament {
        animation: none;
    }
}
</style>
